import { Location } from './element-type';

export interface FromToDetail {
  location: Location;
  pageId?: string;
}
export interface FromTo {
  from: FromToDetail;
  to: FromToDetail;
}

export class ReportTemplate {
  public id: string;
  public name: string;
  public application: string; // generic / visol
  public subjectModelIds: string[];
  public schemaVersion: string;
  public template: TemplateDefinition;
  public isValid: boolean;
  public reportTitleInputMode: string;
  public reportTitleFormat: ReportTitleFormat[];
  public etag: string;
  public createdBy: string;
  public createDate: string;
  public lastUpdatedByUserId: string;
  public lastUpdateDate: string;
  public reportTemplateContent?: string;
  public reportType: string;
  public apiCode: string;
  public equipmentTypes?: string | string[];
  public equipmentType?: string | string[];
  public lastUpdatedDateUtc: string;
}

export class NewReportTemplateInfo {
  public reportType: string;
  public apiCode?: string;
  public equipmentTypes?: string | string[];
  public templateContent: object;
}

export class ReportTitleFormat {
  public value: string;
  public type: string;
  public format: string;
}

export class ListFormat {
  public name: string;
  public id?: string;
  public listData: string[];
}

export class TemplateDefinition {
  public header: TemplateCommonSection;
  public bodies: TemplatePageSection[];
  public footer: TemplateCommonSection;
  public pageMarginTop: number;
  public pageMarginBottom: number;
  public pageMarginLeft: number;
  public pageMarginRight: number;
  public paperSize: string;
  public sizeUnit: string;
  public fontSizeUnit: string;
  public fontSize: number;
  public fontFamily: string;
  public list: ListFormat[];
}

export class TemplateCommonSection {
  public id: string;
  public height: number;
  public texts: TemplateText[];
  public images: TemplateImage[];
  public lines: TemplateLine[];
  public rectangles: TemplateRectangle[];
  public inspectionSummary: TemplateSummary;
  public inspectionPictures: TemplateSummary;
  public dataPlates: TemplateDataPlate;
  public signatures: TemplateSignature[];
  public tables: TemplateTable[];
}

export class TemplatePageSection {
  public id: string;
  public height?: number;
  public hasHeader: boolean;
  public hasFooter: boolean;
  public texts?: TemplateText[];
  public images?: TemplateImage[];
  public lines?: TemplateLine[];
  public rectangles?: TemplateRectangle[];
  public inspectionSummary?: TemplateSummary;
  public subTotal?: TemplateSubTotal;
  public inspectionPictures?: TemplateSummary;
  public dataPlates?: TemplateDataPlate;
  public measurementPoints?: TemplateMeasurementPoint[];
  public visualEvents?: TemplateVisualEvents[];
  public signatures?: TemplateSignature[];
  public sections?: TemplateSection[];
  public tables?: TemplateTable[];
  public interactiveIso?: TemplateIso[];
  public dropDownLists?: TemplateDropDownList[];
  public customElements?: TemplateCustomElements[];
}

export class TemplateCustomPageSection {
  public sections?: TemplateSection[];
  public texts?: TemplateText[];
  public images?: TemplateImage[];
  public lines?: TemplateLine[];
  public rectangles?: TemplateRectangle[];
  public dropDownLists?: TemplateDropDownList[];
  public tables?: TemplateTable[];
}

export class SectionedTemplatePage extends TemplatePageSection {
  public sectionId: string;
  public sectionLeft: number;
  public sectionTop: number;
  public sectionWidth: number;
  public sectionHeight: number;
  public zIndex: number;
  public stageId: string;
}

export class ElementLocationInSection {
  public x: string;
  public y: string;
}

export class SectionInfo {
  public sectionId?: string;
  public sectionStageTag?: string;
  public elementLocationInSection?: ElementLocationInSection;
}

export class TemplateTextItem extends SectionInfo {
  public id: string;
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public padding: string;
  public paddingTop: number;
  public paddingRight: number;
  public paddingBottom: number;
  public paddingLeft: number;
  public zIndex: number;
  public text: string;
  public fontSize: number;
  public fontFamily: string;
  public fontColor: string;
  public fontStyle: string;
  public fontWeight: string;
  public lineHeight: number;
  public horizontalAlign: string;
  public isHorizontal: boolean;
  public verticalAlign: string;
  public borderWidth: number;
  public borderStyle: string;
  public borderColor: string;
  public backgroundColor: string;
}

export class TemplateText extends TemplateTextItem {
  public type: string;
  public format: string;
  public dynamicDataField: string;
  public dynamicId: string;
  public inputType: string;
  public editableId: string;
  public validation: Validation;
  public radioType: string;
  public radioLabel: string;
  public options: Options[];
  public label: string;
  public value: string;
  public inputGroup: string;
  public multiSelectDelimiter: string;
  public isTableText?: boolean;
}

export class TemplateImage extends SectionInfo {
  public id: string;
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public zIndex: number;
  public src: string;
  public type: string;
  public dynamicDataField: string;
  public dynamicId: string;
}

export class TemplateIso extends SectionInfo {
  public id: string;
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public isoType: 'Redline' | 'VisualEvent';
  public dynamicDataField: string;
}

export class TemplateLine extends SectionInfo {
  public id: string;
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public zIndex: number;
  public borderStyle: string;
  public borderColor: string;
  public length: number;
  public isHorizontal: boolean;
}

export class TemplateRectangle extends SectionInfo {
  public id: string;
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public zIndex: number;
  public borderWidth: number;
  public borderStyle: string;
  public borderColor: string;
  public backgroundColor: string;
}

export class TemplateSection {
  public id: string;
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public zIndex: number;
  public stageId: string;
}

export class TemplateTable extends SectionInfo {
  public id: string;
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public tableType: string;
  public tableTitle?: string;
  public headerHeight: number;
  public headerBackgroundColor: string;
  public fontSize: number;
  public cellBorder: string;
  public cellPadding: number;
  public isStriped: boolean;
  public type: string;
  public dynamicDataField?: string;
  public columnList?: ColumnList[];
  public rowList?: RowList[];
  public priorityList?: PriorityList[];
}

export class ColumnList {
  public label: string;
  public width: number;
  public inputType?: string;
  public textOrientation?: string;
  public tagging?: string;
  public textAlignment?: string;
  public needsPriority?: boolean;
  public dataField?: string;
  public requiredField?: boolean;
}

export class RowList {
  public label: string;
  public tagging?: string;
  public textAlignment?: string;
  public isCategory?: boolean;
  public requiredField?: boolean;
}

export class PriorityList {
  public code: string;
  public label: string;
}

export class TextList {
  public text: string;

  public constructor(text: string) {
    this.text = text;
  }
}

export class TextInfoList {
  public textsInfo: TemplateText;

  public constructor(textsInfo: TemplateText){
    this.textsInfo = textsInfo;
  }
}

export class TemplateSummaryTable extends SectionInfo {
  public id: string;
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public zIndex: number;
  public headerHeight: number;
  public headerBackgroundColor: string;
  public headerFontSize: number;
  public headerFontFamily: string;
  public horizontalAlign: string;
  public borderWidth: number;
  public borderStyle: string;
  public borderColor: string;
  public hasBorderTop: boolean;
  public hasBorderBottom: boolean;
  public hasBorderLeft: boolean;
  public hasBorderRight: boolean;
  public hasRowBackground: boolean;
  public rowBackgroundColor: string;
  public columns: TableColumn[];
  public tableMode: boolean;
  public dataMode: number;
}

export abstract class TableColumn {
  public header: string;
  public headerType: string;
  public texts: string[];
  public textsInfo: TemplateText[];
  public fontSize: number;
  public fontFamily: string;
  public horizontalAlign: string;
  public verticalAlign: string;
  public padding: string;
  public paddingTop: number;
  public paddingRight: number;
  public paddingBottom: number;
  public paddingLeft: number;
  public widthRatio: number;
  public isHeaderVertical: boolean;
  public isColumnVertical: boolean;
  public isCombined: boolean;
  public isItemsHorizontal: boolean;
}

export class RequestTableColumn extends TableColumn {
  public textsInfo: TemplateText[];

  public constructor() {
    super();
  }
}

export class DisplayTableColumn extends TableColumn {
  public textsInfo: TemplateText[];

  public constructor() {
    super();
  }
}

export class TemplateMeasurementPoint extends SectionInfo {
  public id: string;
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public zIndex: number;
  public fontSize: number;
  public fontFamily: string;
  public headerHeight: number;
  public headerBackgroundColor: string;
  public headerFontSize: number;
  public headerFontFamily: string;
  public horizontalAlign: string;
  public hasBorderTop: boolean;
  public hasBorderBottom: boolean;
  public hasBorderLeft: boolean;
  public hasBorderRight: boolean;
  public hasRowBackground: boolean;
  public rowBackgroundColor: string;
  public columns: ListItem[];
  public tableBorderWidth: number;
  public tableBorderStyle: string;
  public tableBorderColor: string;
  public tableMargin: number;
  public tableRules: string;
  public cellMargin: number;
  public isStriped: boolean;
  public dynamicDataField: string;
  public tableWidth?: number;
  public titleText: string;
  public titleFontSize: number;
  public titleAlign: string;
  public titleBackgroundColor: string;
}

export class ListItem {
  public label: string;
  public dataField?: string;
  public type?: 'Dynamic' | 'Editable';
  public inputType?: string;
  public optionList?: Array<string>;
  public widthRatio?: number;
}

export class TemplateVisualEvents extends SectionInfo {
  public id: string;
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public zIndex: number;
  public fontSize: number;
  public fontFamily: string;
  public dynamicDataField: string;
  public itemDataSource: string;
  public itemWidth: number;
  public itemHeight: number;
  public borderWidth: number;
  public borderStyle: string;
  public borderColor: string;
  public itemMargin: number;
  public maxItemCount: number;
  public captionsList: ListItem[];
  public itemContainerHeight?: number;
  public titleText: string;
  public titleFontSize: number;
  public titleAlign: string;
  public titleBackgroundColor: string;
}

export class TemplateCustomElements extends SectionInfo {
  public id: string;
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public zIndex: number;
  public fontSize: number;
  public fontFamily: string;
  public dynamicDataField: string;
  public itemDataSource: string;
  public itemWidth: number;
  public itemHeight: number;
  public borderWidth: number;
  public borderStyle: string;
  public borderColor: string;
  public itemMargin: number;
  public maxItemCount: number;
  public captionsList: ListItem[];
  public itemContainerHeight?: number;
  public titleText: string;
  public titleFontSize: number;
  public titleAlign: string;
  public titleBackgroundColor: string;
  public designElementId: string;
  public designElementName: string;
}

export class TemplateSignature extends SectionInfo {
  public id: string;
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public zIndex: number;
  public borderWidth: number;
  public borderStyle: string;
  public borderColor: string;
  public reservedFor: string;
}

export class TemplateSummary extends SectionInfo {
  public id: string;
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public zIndex: number;
  public defaultPageBreakRowCount: number;
  public isEmptyFolderDisplayed: boolean;
  public hasInspectionSummaryLink: boolean;
  public title: TemplateText;
  public table: TemplateSummaryTable;
}

export class TemplateSubTotal extends SectionInfo {
  public id: string;
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public zIndex: number;
  public title: TemplateText;
  public table: TemplateSummaryTable;
}

export class TemplateDataPlate extends SectionInfo {
  public id: string;
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public zIndex: number;
  public title: TemplateText;
  public imageInfo: TemplateDataPlateImageInfo;
}
export class TemplateDataPlateText extends TemplateTextItem {
  public position: string;
}
export class TemplateDataPlateImageInfo extends SectionInfo {
  public id: string;
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public zIndex: number;
  public title: TemplateDataPlateText;
  public image: TemplateImage;
}

export class TemplateDropDownList extends SectionInfo {
  public id: string;
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public zIndex: number;
  public borderWidth: number;
  public borderStyle: string;
  public borderColor: string;
  public selectedValue: string;
  public options: Options[];
}

export class ReportTemplateNew {
  public name: string;
  public subjectModelIds: string[];
  public template: TemplateDefinition;
  public isValid: boolean;
  public reportTitleInputMode: string;
  public reportTitleFormat: ReportTitleFormat[];
  public schemaVersion: string;
  // etag: string;
}

export class ReportTemplateUpdate {
  public id: string;
  public name: string;
  public subjectModelIds: string[];
  public template: TemplateDefinition;
  public isValid: boolean;
  public reportTitleInputMode: string;
  public reportTitleFormat: ReportTitleFormat[];
  public schemaVersion: string;
  public etag: string;
  public eTag: string;

  public application?: string;
  public templateUrl?: string;

  public createdByTenantId?: string;
  public createdByUserId?: string;
  public createdDateUtc?: Date;
  public lastUpdatedDateUtc?: Date;
  public lastUpdatedByUserId?: string;
  public reportTemplateContent?: string;
  public equipmentTypes?: string | string[];
  public equipmentType?: string | string[];
  public reportType?: string;
  public apiCode?: string;
}

export class ReportTemplateList {
  public id: string;
  public name: string;
  public reportTitleInputMode: string;
  public reportTitleFormat: ReportTitleFormat[];
  public application?: string;
  public templateUrl?: string;
  public createdByTenantId?: string;
  public createdByUserId?: string;
  public createdDateUtc?: string;
  public lastUpdatedDateUtc?: string;
  public lastUpdatedByUserId?: string;
  public reportTemplateContent?: string;
  public subjectModelIds?: object;
  public action?: string;
  public reportType?: string;
  public equipmentTypes?: string | string[];
  public equipmentType?: string | string[];
  public apiCode?: string;
  public isValid?: boolean;
}

export class EditableTextList {
  public editableId: string;
  public inputType: string;
  public label: string;
  public radioLabel: string;
  public value: string;
  public text: string;
  public validation: Validation;
  public options: Options[];
  public inputGroup: string;
}

export class Validation {
  public required: boolean;
  public maxLength: number;
}

export class Options {
  public label: string;
  public value: string;
}

export class UpdateIdList {
  public subjectModelIds: string[];
  public etag: string;
}

export class UpdateResultList {
  public updateResult: ResponseIdList[];
  public etag: string;
}

export class ResponseIdList {
  public reportTemplateId: string;
  public subjectModelId: string;
}

export class ReportTemplateImageList {
  public fileName: string;
  public originalUrl: string;
  public thumbUrl: string;
}

export class ReportGlobalImage {
  public id: string;
  public name: string;
  public createdBy: string;
  public dateCreatedUtc: string;
  public dateUpdatedUtc: string;
  public imageUrl: string;
}

export class AddCustomElement {
  public name: string;
  public tenantid: string;
  public application?: string;
  public templateUrl: string;
}

export class ReportCustomElements {
  public id: string;
  public tenantId: string;
  public name: string;
  public application: string;
  public templateUrl: string;
}
